import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_DOT = {
  success: 'bg-emerald-400',
  error: 'bg-red-400',
  unscheduled: 'bg-slate-500',
  pending: 'bg-slate-400',
};

// Worst first, for the collapsed preview (#262). `failing` and `unscheduled`
// are things to act on; `declined` and `never_ran` are things we simply have no
// evidence about. `healthy` never appears in the preview.
const HEALTH_ORDER = ['failing', 'unscheduled', 'declined', 'never_ran', 'healthy'];

const formatRelative = (iso) => {
  if (!iso) return 'never';
  try {
    const diff = new Date(iso).getTime() - Date.now();
    const abs = Math.abs(diff);
    const mins = Math.round(abs / 60000);
    const hours = Math.round(abs / 3600000);
    const days = Math.round(abs / 86400000);
    const label =
      abs < 60000 ? 'just now' :
      mins < 60 ? `${mins}m` :
      hours < 48 ? `${hours}h` :
      `${days}d`;
    return diff > 0 ? `in ${label}` : `${label} ago`;
  } catch {
    return iso;
  }
};

const SchedulerHealthWidget = () => {
  const [data, setData] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);
  const [buHealth, setBuHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [triggering, setTriggering] = useState(null); // job_id currently being triggered
  const [toast, setToast] = useState(null); // {type, text}
  // Per-job history drawer state
  const [drawerJobId, setDrawerJobId] = useState(null);
  const [drawerRuns, setDrawerRuns] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const openHistoryDrawer = useCallback(async (jobId) => {
    setDrawerJobId(jobId);
    setDrawerLoading(true);
    setDrawerRuns([]);
    try {
      const res = await axios.get(`${API}/schedulers/jobs/${jobId}/history?limit=20`);
      setDrawerRuns(res.data?.runs || []);
    } catch {
      setDrawerRuns([]);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const closeHistoryDrawer = useCallback(() => {
    setDrawerJobId(null);
    setDrawerRuns([]);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [healthRes, errorsRes, buRes] = await Promise.all([
        axios.get(`${API}/schedulers/health`),
        axios.get(`${API}/schedulers/recent-errors?limit=5`),
        axios.get(`${API}/bu/health`).catch(() => ({ data: null })),
      ]);
      setData(healthRes.data);
      setRecentErrors(errorsRes.data?.errors || []);
      setBuHealth(buRes.data);
    } catch {
      // silent fail — widget is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, [fetchAll]);

  const runNow = useCallback(async (jobId) => {
    setTriggering(jobId);
    setToast(null);
    try {
      const res = await axios.post(`${API}/schedulers/run/${jobId}`);
      setToast({ type: 'success', text: `Triggered ${jobId} on ${res.data?.scheduler} scheduler` });
      // Refresh after a short delay to reflect the updated next_run / last_status
      setTimeout(fetchAll, 1500);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Run failed';
      setToast({ type: 'error', text: `${jobId}: ${msg}` });
    } finally {
      setTriggering(null);
      setTimeout(() => setToast(null), 4000);
    }
  }, [fetchAll]);

  if (loading || !data) return null;

  const { total_jobs, healthy_jobs, unhealthy_jobs, jobs = [] } = data;
  const hasErrors = jobs.some((j) => j.last_status === 'error');

  return (
    <div
      className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
      data-testid="scheduler-health-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white">Scheduler Health</h3>
          {hasErrors && (
            <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full" data-testid="scheduler-health-error-badge">
              {jobs.filter((j) => j.last_status === 'error').length} failing
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400" data-testid="scheduler-health-count">
            {healthy_jobs}/{total_jobs} healthy
          </span>
          <button
            onClick={() => setExpanded((e) => !e)}
            className="text-xs text-emerald-400 hover:text-emerald-300"
            data-testid="scheduler-health-toggle"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {buHealth?.business_units?.length > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40"
          data-testid="bu-health-badge"
        >
          <span className="text-xs uppercase tracking-wide text-slate-400">DB Health</span>
          {buHealth.business_units.map((bu) => {
            const ok = bu.status === 'green';
            return (
              <div
                key={bu.bu}
                className="flex items-center gap-1.5 text-xs"
                data-testid={`bu-health-${bu.bu.toLowerCase()}`}
                title={
                  `${bu.label}\nping=${bu.ping}\nprimary_index=${bu.primary_index}\n` +
                  `collections_with_data=${bu.collections_with_data}\n` +
                  `latency=${bu.latency_ms ?? 'n/a'}ms` +
                  (bu.error ? `\nerror=${bu.error}` : '')
                }
              >
                <span
                  className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-400' : 'bg-red-400'}`}
                  data-testid={`bu-health-dot-${bu.bu.toLowerCase()}`}
                />
                <span className="font-medium text-white">{bu.bu}</span>
                <span className="text-slate-400">
                  {bu.latency_ms !== null && bu.latency_ms !== undefined ? `${bu.latency_ms}ms` : 'n/a'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {toast && (
        <div
          className={`mb-3 px-3 py-2 text-xs rounded-md ${
            toast.type === 'success'
              ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
              : 'bg-red-500/10 text-red-300 border border-red-500/30'
          }`}
          data-testid="scheduler-run-toast"
        >
          {toast.text}
        </div>
      )}

      {unhealthy_jobs > 0 && !expanded && (
        <div className="space-y-2">
          {jobs
            /* #262: `health` is the server's own classification. The old filter
               was error-or-unscheduled, which after the health count became
               strict could leave this preview empty while the summary above
               said jobs were unhealthy. Worst first. */
            .filter((j) => j.health && j.health !== 'healthy')
            .sort((a, b) => HEALTH_ORDER.indexOf(a.health) - HEALTH_ORDER.indexOf(b.health))
            .slice(0, 3)
            .map((j) => (
              <JobRow key={`${j.scheduler}-${j.job_id}`} job={j} onRun={runNow} triggering={triggering} onOpenHistory={openHistoryDrawer} />
            ))}
        </div>
      )}

      {expanded && (
        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="scheduler-health-job-list">
          {jobs.map((j) => (
            <JobRow key={`${j.scheduler}-${j.job_id}`} job={j} onRun={runNow} triggering={triggering} onOpenHistory={openHistoryDrawer} />
          ))}
        </div>
      )}

      {unhealthy_jobs === 0 && !expanded && (
        <p className="text-sm text-slate-500" data-testid="scheduler-health-all-ok">
          All {total_jobs} scheduled jobs ran successfully. Last refresh {formatRelative(data.generated_at)}.
        </p>
      )}

      {/* Last 5 errors ticker */}
      <div className="mt-5 pt-4 border-t border-slate-800" data-testid="scheduler-recent-errors">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wide text-slate-400">Recent errors (24h+)</h4>
          <span className="text-xs text-slate-500">last {recentErrors.length || 0}</span>
        </div>
        {recentErrors.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="scheduler-recent-errors-empty">
            No scheduler errors on record. Clean run.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {recentErrors.map((e, i) => (
              <li
                key={`${e.job_id}-${e.event_time}-${i}`}
                className="text-xs text-slate-300 flex items-start gap-2"
                data-testid={`scheduler-recent-error-${i}`}
              >
                <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-red-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white truncate">{e.job_id}</span>
                    <span className="text-slate-500">{formatRelative(e.event_time)}</span>
                  </div>
                  <p className="text-red-400/80 truncate" title={e.error}>{e.error}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Per-job run history drawer — slides in from the right */}
      {drawerJobId && (
        <div
          className="fixed inset-0 z-50"
          data-testid="scheduler-history-drawer"
          onClick={closeHistoryDrawer}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <aside
            className="absolute top-0 right-0 h-full w-full max-w-md bg-slate-950 border-l border-slate-800 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="scheduler-history-panel"
          >
            <div className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800 px-5 py-4 flex items-center justify-between z-10">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-slate-400">Run history</p>
                <h3 className="text-base font-semibold text-white truncate" title={drawerJobId}>
                  {drawerJobId}
                </h3>
              </div>
              <button
                onClick={closeHistoryDrawer}
                className="text-slate-400 hover:text-white text-lg leading-none px-2"
                data-testid="scheduler-history-close"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-2">
              {drawerLoading && (
                <p className="text-sm text-slate-500" data-testid="scheduler-history-loading">Loading…</p>
              )}
              {!drawerLoading && drawerRuns.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="scheduler-history-empty">
                  No runs recorded yet for this job.
                </p>
              )}
              {!drawerLoading && drawerRuns.map((r, i) => (
                <div
                  key={`${r.event_time}-${i}`}
                  className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/60 border border-slate-800"
                  data-testid={`scheduler-history-run-${i}`}
                >
                  <span
                    className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                      r.status === 'success' ? 'bg-emerald-400' :
                      r.status === 'error' ? 'bg-red-400' : 'bg-slate-500'
                    }`}
                  />
                  <div className="min-w-0 flex-1 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-medium ${
                        r.status === 'success' ? 'text-emerald-300' :
                        r.status === 'error' ? 'text-red-300' : 'text-slate-300'
                      }`}>
                        {r.status || 'n/a'}
                      </span>
                      <span className="text-slate-500">{formatRelative(r.event_time)}</span>
                    </div>
                    <div className="text-slate-400 mt-0.5">
                      {r.duration_ms !== null && r.duration_ms !== undefined ? (
                        <span>Duration: <span className="text-slate-300">{r.duration_ms}ms</span></span>
                      ) : (
                        <span className="text-slate-500">Duration: n/a</span>
                      )}
                    </div>
                    {r.error && (
                      <p className="text-red-400/80 mt-1 break-words" title={r.error}>
                        {r.error}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

const JobRow = ({ job, onRun, triggering, onOpenHistory }) => {
  const status = !job.next_run ? 'unscheduled' : (job.last_status || 'pending');
  const dot = STATUS_DOT[status] || STATUS_DOT.pending;
  const isTriggering = triggering === job.job_id;
  return (
    <div
      className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-slate-600/60 transition-colors"
      data-testid={`scheduler-job-${job.job_id}`}
    >
      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot}`}></span>
      <button
        onClick={() => onOpenHistory && onOpenHistory(job.job_id)}
        className="min-w-0 flex-1 text-left"
        data-testid={`scheduler-history-open-${job.job_id}`}
        title="Click to view last 20 runs"
      >
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-white truncate hover:underline decoration-dotted underline-offset-2">{job.name}</p>
          <span className="text-xs text-slate-500">[{job.scheduler}]</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
          <span>Next: {formatRelative(job.next_run)}</span>
          <span>·</span>
          <span>
            Last:{" "}
            <span className={
              job.last_status === 'error' ? 'text-red-400' :
              job.last_status === 'success' ? 'text-emerald-400' :
              'text-slate-500'
            }>
              {job.last_status || 'n/a'}
            </span>
            {job.last_completed_at && ` (${formatRelative(job.last_completed_at)})`}
          </span>
        </div>
        {job.last_error && (
          <p className="text-xs text-red-400/80 mt-1 truncate" title={job.last_error}>
            {job.last_error}
          </p>
        )}
      </button>
      <button
        onClick={() => onRun(job.job_id)}
        disabled={isTriggering}
        className="ml-2 px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        data-testid={`scheduler-run-now-${job.job_id}`}
        title="Trigger this job to run immediately"
      >
        {isTriggering ? 'Running…' : 'Run now'}
      </button>
    </div>
  );
};

export default SchedulerHealthWidget;
